/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import { IGroup, IGroupDelivery, IGroups, IOrderInformation, ISalesLine } from '@msdyn365-commerce-modules/order-management/src/common';

import { IOrderConfirmationViewProps } from '@msdyn365-commerce-modules/order-management/src/modules/order-confirmation/';
import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import React, { useEffect, useState } from 'react';

import {
    IHelp,
    IOrderSummary,
    IPaymentMethods
} from '@msdyn365-commerce-modules/order-management/src/modules/order-confirmation/../../common';
import { getProductUrlSync, IFullOrgUnitAvailability } from '@msdyn365-commerce-modules/retail-actions';
import { PriceComponent } from '@msdyn365-commerce/components';
import { IAny, ICoreContext, IGeneric, IActionContext } from '@msdyn365-commerce/core';
import {
    CommerceProperty,
    CommercePropertyValue,
    ProductDeliveryOptions,
    ProductSearchCriteria,
    ProductSearchResult,
    SalesLine,
    SalesOrder,
    SimpleProduct
} from '@msdyn365-commerce/retail-proxy';
import { getDeliveryOptionsAsync, searchByCriteriaAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';
import get from 'lodash/get';
import {
    IOrderConfirmationProps,
    IOrderConfirmationResources,
    IPaymentCardImagesData
} from '../definition-extensions/order-confirmation.ext.props.autogenerated';
import DobbiesCartLineItemComponent from './components/cartlineitem.component';
import { getDelayedDeliveryMsg, getDeliveryDateRange } from '../utilities/estDeliveryDate';
import { getAllOrgUnitLocationsByArea, GetAllOrgUnitLocationsByAreaInput } from '../../../actions/get-all-store-location-information';

/* VSI Customization - START - 23/11/20 */
export interface ISalesInfoProps {
    itemCount: React.ReactElement;
    grossAmount: React.ReactElement;
}
export interface IExtendedOrderSummary extends IOrderSummary {
    orderSummaryitemsCount?: number;
    isCnCCart?: boolean;
}
export interface IOrderDate {
    dateLabel: string;
    orderCreatedDate: string;
}

export interface IVendorProducts {
    name: string | undefined;
    time: string | undefined;
    productList: number[];
}
export interface DeliveryAddressShip {
    props: {
        deliveryType: string;
        address: {
            Name: string;
            Street: string;
            City: string;
            ZipCode: string;
            Phone: string;
        };
    };
}

interface IExtendedPaymentProps extends IPaymentMethods {
    paymentCardImages?: IPaymentCardImagesData[];
    groups?: IGroups;
    salesOrder?: SalesOrder;
}

interface IOrderConfirmationGroupPickupGuestProps {
    delivery: IGroupDelivery;
    context: ICoreContext<IGeneric<IAny>>;
}
/* VSI Customization - END - 18/12/20 */
// remove customixation for .29 version
// Converting functional component to simple function to reduce cycolmatic complexity by adding undefined in props and checking condition in functio instead of main FC
// tslint:disable-next-line:no-any
// const _orderConfirmationOrderSummary = (props: IOrderSummary | undefined, salesOrder: any) => {
//     if (!props) {
//         return;
//     }
//     const { orderSummaryProps, heading, subtotal, totalAmount, earnedPoints } = props;
//     const shippingChargeAmount = salesOrder.ShippingChargeAmount;
//     return (
//         <Node {...orderSummaryProps}>
//             {heading}
//             {subtotal}
//             {}
//             {/* {shipping} */}
//             <p className='ms-order-confirmation__order-summary-line-subtotal'>
//                 <span className='ms-order-confirmation__order-summary-label'>{'Home Delivery'}</span>
//                 <span className='msc-price msc-order-msc-price ms-order-confirmation__order-summary-price'>
//                     <span className='msc-price__actual'>{shippingChargeAmount > 0 ? `£${shippingChargeAmount.toFixed(2)}` : '£0.00'}</span>
//                 </span>
//             </p>
//             {totalAmount}
//             {earnedPoints}
//         </Node>
//     );
// };

const extractTextFromMethods = (node: React.ReactNode): string => {
    if (typeof node === 'string') return node;
    if (Array.isArray(node)) return node.map(extractTextFromMethods).join('');
    if (React.isValidElement(node)) return extractTextFromMethods(node.props.children);
    return '';
};

const formatCardLabel = (label: string): string => {
    const parts = label.split('ending');
    if (parts.length === 2) {
        const cardType = parts[0].trim();
        const last4 = parts[1].trim();
        return `${cardType} *${last4}`;
    }
    return label;
};

const getCardIcon = (label: string, paymentCardImages: any[]): string => {
    if (!label || !paymentCardImages || paymentCardImages.length === 0) return '';

    const labelName = label.toLowerCase();

    const matchedImage = paymentCardImages.find(img => img?.cardLabel?.toLowerCase() && labelName.includes(img.cardLabel.toLowerCase()));

    return matchedImage?.cardImage?.src || '';
};

export const OrderConfirmationPayment: React.FC<IExtendedPaymentProps> = ({
    paymentMethodsProps,
    title,
    methods,
    paymentCardImages,
    groups,
    salesOrder
}) => {
    const rawText = extractTextFromMethods(methods);
    const cardIconSrc = getCardIcon(rawText, paymentCardImages || []);
    const formattedLabel = formatCardLabel(rawText);

    const delivery_ship = groups?.groups.find(addr => {
        const deliveryAddress = addr.delivery?.address as DeliveryAddressShip | undefined;
        const deliveryType = deliveryAddress?.props?.deliveryType;
        return deliveryType && deliveryType.trim().toLowerCase() === 'ship';
    });

    const deliveryAddressShip = delivery_ship?.delivery?.address as DeliveryAddressShip | undefined;
    const payment_address_name = deliveryAddressShip?.props?.address.Name;
    const payment_address_street = deliveryAddressShip?.props?.address.Street;
    const payment_address_city = deliveryAddressShip?.props?.address.City;
    const payment_address_phone = deliveryAddressShip?.props?.address.Phone;
    const receiptEmail = salesOrder?.ReceiptEmail ? salesOrder?.ReceiptEmail : '';
    return (
        <div className='order-confirmation__payment'>
            <h5>Payment information</h5>
            {/* <Node {...paymentMethodsProps}> */}
            {/* VSI-Customization -- START
        Payment not required on order-confirmation page as per design
        */}
            {/* {title} */}
            {/* {methods} */}

            <div className='order-confirmation__payment-card'>
                <img src={cardIconSrc} alt={formattedLabel} />
                <p>{formattedLabel}</p>
            </div>
            {/* VSI-Customization -- END */}
            {/* </Node> */}
        </div>
    );
};
// Converting functional component to simple function to reduce cycolmatic complexity by adding undefined in props and checking condition in functio instead of main FC
const _orderConfirmationHelp = (props: IHelp | undefined) => {
    if (!props) {
        return;
    }
    const { helpProps, needHelpLabel, helpLineNumberLabel, contactNumber } = props;
    return (
        <Node {...helpProps}>
            {needHelpLabel}
            {helpLineNumberLabel}
            {contactNumber}
        </Node>
    );
};
/* VSI Customization - START - 23/11/20 */
const _getCountandPrice = (props: IOrderConfirmationViewProps & IOrderConfirmationProps<{}>): ISalesInfoProps => {
    const {
        data: { orderHydration },
        resources: { itemLabel, itemsLabel, orderTotalLabelText }
    } = props;
    const salesOrder = orderHydration && orderHydration.result && orderHydration.result.salesOrder;
    // items count
    let itemsCount = 0;
    salesOrder &&
        salesOrder.SalesLines &&
        salesOrder.SalesLines.map(salesLine => {
            const quantity = salesLine.Quantity;
            itemsCount = quantity ? itemsCount + quantity : itemsCount;
        });
    // format price
    const grossAmount = salesOrder && salesOrder.GrossAmount;
    const totalAmount =
        grossAmount && props.context.cultureFormatter.formatCurrency(grossAmount.toString(), props.context.request.channel?.Currency);
    return {
        itemCount: (
            <Node className='__sales-order-count' tag='div'>
                <Node className='label' tag='span'>
                    {itemsCount}
                </Node>
                <Node className='value' tag='span'>
                    {itemsCount === 1 ? itemLabel : itemsLabel}
                </Node>
            </Node>
        ),
        grossAmount: (
            <Node className='__sales-order-total' tag='div'>
                <Node className='label' tag='span'>
                    {orderTotalLabelText}
                </Node>
                <Node className='value' tag='span'>
                    {totalAmount}
                </Node>
            </Node>
        )
    };
};

export const OrderConfirmationOrderInfomation: React.FC<IOrderConfirmationViewProps &
    IOrderConfirmationProps<{}> &
    IOrderInformation &
    ISalesInfoProps &
    IOrderDate> = props => {
    const {
        orderInformationProps,
        channelReferenceId,
        itemCount,
        grossAmount,
        dateLabel,
        orderCreatedDate,
        resources: { orderIdLabel, orderPlacedOnLabel }
    } = props;
    const { isAuthenticated } = props?.['context'].actionContext.requestContext.user;
    const orderno_no = get(channelReferenceId, 'props.children[0].props.children[1].props.children');
    const itemCount_str = get(itemCount, 'props.children[1].props.children');
    const itemCount_no = get(itemCount, 'props.children[0].props.children');
    const gross_amount = get(grossAmount, 'props.children[1].props.children');
    if (isAuthenticated) {
        return (
            <Node {...orderInformationProps}>
                {/* VSI Customization - START - 23/11/20 */}
                {/* <div>
                    {orderno_str}
                    {orderno_no}
                </div> */}

                {/* VSI Customization - END - 27/11/20 */}
                {/* {channelReferenceId} */}

                <div className='__order-brief-info'>
                    <div className='ord-number'>
                        <h3>
                            {/* {orderIdLabel} */}
                            Reference number:
                            <span>{orderno_no}</span>
                        </h3>
                    </div>
                    <div className='__order-information-created-date ss'>
                        <Node className='__created-date' tag='span'>
                            <strong>Order date:</strong>
                        </Node>
                        <Node className='__created-date-label' tag='span'>
                            {orderCreatedDate}
                        </Node>
                    </div>

                    <div className='count-ammount'>
                        {itemCount}

                        <span className='gross-amount'> {gross_amount}</span>
                    </div>
                </div>
            </Node>
        );
    } else {
        return (
            <>
                <div className='order-info'>
                    <h3>
                        {/* {orderIdLabel} */}
                        Reference number:
                        <span>{orderno_no}</span>
                    </h3>
                    <p>
                        Order date:
                        <span> {orderCreatedDate}</span>
                    </p>
                    <p>
                        {' '}
                        {itemCount_no} {itemCount_str} <span>{gross_amount}</span>
                    </p>
                </div>
            </>
        );
    }
};

/* VSI Customization - START - 23/11/20 - Removed OrderConfirmationSalesLine FC -  END */

export const OrderConfirmationGroupDelivery: any = (props: IDobbiesOrderConfirmationViewProps & IOrderConfirmationProps<{}>) => {
    const delivery_ship = props.groups?.groups.find(addr => {
        const deliveryAddress = addr.delivery?.address as DeliveryAddressShip | undefined;
        const deliveryType = deliveryAddress?.props?.deliveryType;
        return deliveryType && deliveryType.trim().toLowerCase() === 'ship';
    });

    const receiptEmail = props.salesOrder?.ReceiptEmail ? props.salesOrder?.ReceiptEmail : '';

    let itemCount_no = 0;
    props.salesLines?.map(item => {
        if (item.data.deliveryType === 'ship') {
            itemCount_no += item?.data?.salesLine?.Quantity || 0;
        }
    });

    let shippingCost = 0;
    props.salesOrder?.ChargeLines?.map(chargeline => {
        shippingCost += chargeline.CalculatedAmount || 0;
    });

    const bothDeliveries = [delivery_ship];
    console.log('bothDeliveries', bothDeliveries);
    console.log('bothDeliveries props', props);
    return (
        <div>
            {bothDeliveries.length &&
                bothDeliveries?.map((addr, index) => {
                    const deliveryAddressShip = addr?.delivery?.address as DeliveryAddressShip | undefined;
                    if (addr && deliveryAddressShip?.props?.deliveryType === 'ship') {
                        const address_name = deliveryAddressShip?.props?.address.Name;
                        const address_street = deliveryAddressShip?.props?.address.Street;
                        const address_city = deliveryAddressShip?.props?.address.City;
                        const address_zipcode = deliveryAddressShip?.props?.address.ZipCode;
                        const address_phone = deliveryAddressShip?.props?.address.Phone;

                        if (itemCount_no === 0) {
                            return null;
                        } else {
                            return (
                                <>
                                    <div className='order-confirmation__HD'>
                                        <h5>{props.resources.orderSummaryShippingFeeLabel}</h5>
                                        <div className='order-confirmation__HD-item-cost'>
                                            <p>
                                                {itemCount_no} {itemCount_no > 1 ? 'items' : 'item'} | Delivery cost: £{shippingCost}
                                            </p>
                                        </div>
                                        <h6>Address</h6>
                                        <div className='order-confirmation__HD-details'>
                                            <p>{address_name}</p>
                                            <p>{address_street}</p>
                                            <p>{address_city}</p>
                                            <p>{address_zipcode}</p>
                                        </div>
                                        <div className='order-confirmation__HD-contact'>
                                            <p>{receiptEmail}</p>
                                            <p>{address_phone}</p>
                                        </div>

                                        {props.vendorsInfo && _displayVendorProducts(props, props.vendorsInfo, props.deliveryOptions)}
                                    </div>
                                </>
                            );
                        }
                    } else {
                        return <></>;
                    }
                })}
        </div>
    );
    // const storeName = (address as JSX.Element)?.props.address.Name;
    // const storeNameHref: string | undefined = `${storeName?.toLowerCase().replace(/\s+/g, '-')}`;
    // console.log('address', address)
    // return (
    //     <Node {...deliveryProps}>
    //         {/* {heading}
    //     {address} */}

    //        Testing 3

    //     </Node>
    // );
};

export interface IDobbiesOrderConfirmationViewProps extends IOrderConfirmationViewProps {
    deliveryOptions?: ProductDeliveryOptions[];
    vendorsInfo?: any;
}

export const OrderConfirmationGroupDeliveryGuest = (props: IDobbiesOrderConfirmationViewProps & IOrderConfirmationProps<{}>) => {
    const delivery_ship = props.groups?.groups.find(addr => {
        const deliveryAddress = addr.delivery?.address as DeliveryAddressShip | undefined;
        const deliveryType = deliveryAddress?.props?.deliveryType;
        return deliveryType && deliveryType.trim().toLowerCase() === 'ship';
    });

    const receiptEmail = props.salesOrder?.ReceiptEmail ? props.salesOrder?.ReceiptEmail : '';

    let itemCount_no = 0;
    props.salesLines?.map(item => {
        if (item.data.deliveryType === 'ship') {
            itemCount_no += item?.data?.salesLine?.Quantity || 0;
        }
    });
    let shippingCost = 0;
    props.salesOrder?.ChargeLines?.map(chargeline => {
        shippingCost += chargeline.CalculatedAmount || 0;
    });

    const bothDeliveries = [delivery_ship];
    return (
        <div>
            {bothDeliveries.length &&
                bothDeliveries?.map((addr, index) => {
                    const deliveryAddressShip = addr?.delivery?.address as DeliveryAddressShip | undefined;
                    if (addr && deliveryAddressShip?.props?.deliveryType === 'ship') {
                        const address_name = deliveryAddressShip?.props?.address.Name;
                        const address_street = deliveryAddressShip?.props?.address.Street;
                        const address_city = deliveryAddressShip?.props?.address.City;
                        const address_zipcode = deliveryAddressShip?.props?.address.ZipCode;
                        const address_phone = deliveryAddressShip?.props?.address.Phone;

                        if (itemCount_no === 0) {
                            return null;
                        } else {
                            return (
                                <>
                                    <div className='order-confirmation__HD'>
                                        <h5>{props.resources.orderSummaryShippingFeeLabel}</h5>
                                        <div className='order-confirmation__HD-item-cost'>
                                            <p>
                                                {itemCount_no} {itemCount_no > 1 ? 'items' : 'item'} | Delivery cost: £{shippingCost}
                                            </p>
                                        </div>
                                        <h6>Address</h6>
                                        <div className='order-confirmation__HD-details'>
                                            <p>{address_name}</p>
                                            <p>{address_street}</p>
                                            <p>{address_city}</p>
                                            <p>{address_zipcode}</p>
                                        </div>
                                        <div className='order-confirmation__HD-contact'>
                                            <p>{receiptEmail}</p>
                                            <p>{address_phone}</p>
                                        </div>

                                        {props.vendorsInfo && _displayVendorProducts(props, props.vendorsInfo, props.deliveryOptions)}
                                    </div>
                                </>
                            );
                        }
                    } else {
                        return <></>;
                    }
                })}
        </div>
    );
};
export const OrderConfirmationGroupPickupGuest: React.FC<IOrderConfirmationGroupPickupGuestProps> = ({ delivery, context }) => {
    const address = delivery?.address as { delivery: { address: { City: string; FullAddress: string } } } | undefined;
    const address_city = address?.delivery?.address.City;
    const Full_address = address?.delivery?.address.FullAddress;

    return (
        <div className='order-confirmation__CNC-product-storeDetails'>
            <p>
                Collect:
                <span>Dobbies {address_city}</span>
            </p>

            <a href='#'>Store Info</a>
        </div>
    );
};
// Just to display address against group i.e. only picked up group
export const OrderConfirmationGroup: React.FC<IGroup> = props => {
    const { groupProps, delivery } = props;
    return (
        <Node {...groupProps}>
            {delivery && <OrderConfirmationGroupDelivery {...delivery} />}
            {/* VSI Customization - START - 23/11/20 - Displayed sales line in OrderConfirmationGroups function and removed from here - VSI Customization - END */}
        </Node>
    );
};

/* VSI Customization - START - 23/11/20 */
// Following function displays sales line items
const _displaySalesLine = (
    salesLineItem: SalesLine,
    simpleProduct: SimpleProduct,
    props: IOrderConfirmationViewProps & IOrderConfirmationProps<{}>
): React.ReactElement => {
    const { resources } = props;
    // resources required for ICartlineItem component
    const _resources = {
        discountStringText: resources.discountStringLabel,
        sizeString: resources.productDimensionSize,
        colorString: resources.productDimensionColor,
        configString: resources.productDimensionConfiguration,
        styleString: resources.productDimensionStyle,
        quantityDisplayString: resources.quantityLabel,
        inputQuantityAriaLabel: resources.quantityLabel,
        originalPriceText: resources.originalPriceLabel,
        currentPriceText: resources.currentPriceLabel,
        shippingChargesText: resources.shippingChargesLabel
    };
    const _imageSettings = {
        viewports: {
            xs: { q: `w=50&h=50&m=6`, w: 0, h: 0 },
            xl: { q: `w=50&h=50&m=6`, w: 0, h: 0 }
        }
    };
    return (
        <DobbiesCartLineItemComponent
            data={{
                cartLine: salesLineItem,
                product: simpleProduct
            }}
            currentQuantity={salesLineItem.Quantity!}
            maxQuantity={props.app.config.maxQuantityForCartLineItem}
            isOutOfStock={false}
            gridSettings={props.context.request.gridSettings!}
            id={props.id}
            typeName={props.typeName}
            productUrl={getProductUrlSync(simpleProduct, props.context.actionContext, undefined)}
            context={props.context}
            imageSettings={props.config.imageSettings || _imageSettings}
            resources={_resources}
            primaryImageUrl={simpleProduct.PrimaryImageUrl}
            errorMessage={''}
            hideQuantityControls={true}
            isOrderConfirmation={true}
            isQuantityEditable={false}
            isSalesLine={true}
            isOrderConirmationView={true}
            displaySalesLinePrice={_displaySalesLinePrice(salesLineItem, props)}
        />
    );
};
// Following function displayes price with each sales line
const _displaySalesLinePrice = (
    salesLineItem: SalesLine,
    props: IOrderConfirmationViewProps & IOrderConfirmationProps<{}>
): React.ReactElement => {
    const {
        id,
        context,
        typeName,
        resources: { originalPriceLabel, currentPriceLabel }
    } = props;
    return (
        <Node tag='div' className='msc-cart-line-item-subtotal-amount'>
            <PriceComponent
                data={
                    salesLineItem.NetAmountWithoutTax
                        ? {
                              price: {
                                  CustomerContextualPrice: salesLineItem.NetAmountWithoutTax,
                                  BasePrice: salesLineItem.NetPrice
                              }
                          }
                        : {
                              price: {
                                  CustomerContextualPrice: salesLineItem.NetAmountWithAllInclusiveTax,
                                  BasePrice: salesLineItem.Price
                              }
                          }
                }
                context={context}
                id={id}
                typeName={typeName}
                className='discount-value-line'
                originalPriceText={originalPriceLabel}
                currentPriceText={currentPriceLabel}
            />
        </Node>
    );
};
/* VSI Customization - END */

// Converted functional component to simple function to pass order confirmation props to it
// This functions just displays picked up group
const _renderOrderConfirmationGroup = (
    props: IOrderConfirmationViewProps & IOrderConfirmationProps<{}>,
    group: IGroup,
    pickUpOrderNumber: number,
    allStores?: IFullOrgUnitAvailability[]
) => {
    const { salesLines, salesLinesProps, groupProps, delivery } = group;
    const { isAuthenticated } = props.context.request.user;
    return (
        <>
            {salesLines && salesLines.length > 0 && (
                <Node {...groupProps}>
                    <Node {...salesLinesProps}>
                        {salesLines.map((salesLine: ISalesLine, index: number) => {
                            const salesLineItem = salesLine.data.salesLine;
                            const simpleProduct = salesLine.data.product;

                            const storeName = allStores?.find(
                                store => store.OrgUnitAvailability?.OrgUnitLocation?.OrgUnitNumber === salesLineItem.FulfillmentStoreId
                            )?.OrgUnitAvailability?.OrgUnitLocation?.OrgUnitName;
                            const storeNameHref: string | undefined = `${storeName?.toLowerCase().replace(/\s+/g, '-')}`;
                            return (
                                <Node className='sales-line-section' tag='div' key={index}>
                                    {simpleProduct && _displaySalesLine(salesLineItem, simpleProduct, props)}
                                    {/* {_displaySalesLinePrice(salesLineItem, props)} */}
                                    <div className='order-confirmation__CNC-product-storeDetails'>
                                        <p>
                                            Collect:
                                            <span>Dobbies {storeName} </span>
                                        </p>

                                        <a href={`/${storeNameHref}`} target='_blank'>
                                            Store Info
                                        </a>
                                    </div>
                                </Node>
                            );
                        })}
                    </Node>
                    {delivery && isAuthenticated && (
                        <>
                            <OrderConfirmationGroupDelivery {...delivery} />
                        </>
                    )}
                </Node>
            )}
        </>
    );
};

// Following function displays picked up groups only
const _renderPickedOrders = (
    props: IOrderConfirmationViewProps & IOrderConfirmationProps<{}>,
    groups: IGroups,
    allStores?: IFullOrgUnitAvailability[]
) => {
    const {
        resources: { clickandCollectLabel, pickedOrderIndex }
    } = props;
    // filter pickedup groups
    const filteredPickedupGroups = groups.groups.filter(group => {
        const deliveryType = group.data.deliveryType.trim().toLowerCase();
        return deliveryType === 'click and collect' || deliveryType === 'pickup' || deliveryType !== 'ship';
    });
    const pickedUpCount = filteredPickedupGroups.length;
    let itemCount_no = 0;
    props.salesLines?.map(item => {
        if (item.data.deliveryType === 'Click and Collect') {
            itemCount_no += item?.data?.salesLine?.Quantity || 0;
        }
    });
    const freeCost = props.resources.freePriceText;
    const receiptEmail = props.salesOrder?.ReceiptEmail ? props.salesOrder?.ReceiptEmail : '';
    const customerName = props.salesOrder?.CustomerName ? props.salesOrder?.CustomerName : '';
    if (pickedUpCount === 0) {
        return null;
    } else {
        return (
            <div>
                {/* CNC products */}
                <div className='order-confirmation__CNC'>
                    {/* <h5>{clickandCollectLabel}</h5> */}
                    <h5>Click & collect</h5>
                    <div className='order-confirmation__CNC-item-cost'>
                        <p>
                            {itemCount_no} {itemCount_no > 1 ? 'items' : 'item'} | Collection cost: Free
                        </p>
                    </div>
                    {/* <div className='order-confirmation__CNC-details'>
                        <p>{customerName}</p>
                    </div> */}
                    <div className='order-confirmation__CNC-contact'>
                        <p>{receiptEmail}</p>
                        {/* <p>012xxxxxxxxxx</p> */}
                    </div>

                    <p className='order-confirmation__CNC-orderReadyEmail'>
                        We’ll email when your order is ready. <a href={props.context.app.config.cartLineLearnMoreLink || '#'}>Learn more</a>
                    </p>
                    {filteredPickedupGroups.map((group: IGroup, index: number) => {
                        const pickUpOrderNumber = index + 1;
                        const _orderIndex = pickedOrderIndex && pickedOrderIndex.replace('{index}', pickUpOrderNumber.toString());
                        return (
                            <Node className='ms-pickedup-products' tag='div' key={index}>
                                {_renderOrderConfirmationGroup(props, group, pickUpOrderNumber, allStores)}
                            </Node>
                        );
                    })}
                </div>
            </div>
        );
    }
};

/* VSI Customization - END */
const OrderConfirmationView: React.FC<IOrderConfirmationViewProps & IOrderConfirmationProps<{}>> = props => {
    const {
        moduleProps,
        heading,
        alert,
        loading,
        orderInfomation,
        orderSummary,
        payment,
        help,
        groups,
        salesLines,
        context: { actionContext },
        slots: { helpCentreBlock, textBlockBanner },
        data: { orderHydration },
        resources: { orderPlacedOnLabel, dobbiesLabel, notAvailableText }
    } = props;
    const { isAuthenticated } = props.context.request.user;
    const [allStores, setAllStores] = React.useState<IFullOrgUnitAvailability[]>();
    /* VSI Customization - START - 23/11/20 */
    const [vendorProducts, SetVendorProducts] = useState<SimpleProduct[]>([]);
    const [vendorsInfo, SetVendorInfo] = useState<IVendorProducts[]>([]);
    const [deliveryGroup, SetDeliveryGroup] = useState(-1);
    const [pickedUpGroup, SetPickedUpGroup] = useState<number[]>([]);
    const salesOrderInfo = _getCountandPrice(props);
    const salesOrder = orderHydration && orderHydration.result && orderHydration.result.salesOrder;
    const products = orderHydration && orderHydration.result && orderHydration.result.products;
    const [deliveryOptions, setDeliveryOptions] = useState<ProductDeliveryOptions[]>([]);
    // check cnc items
    const isCnCCart = !!salesOrder?.SalesLines?.find(line => line.DeliveryMode === props.context.request.channel?.PickupDeliveryModeCode);

    let orderSummaryitemsCount = 0;
    salesOrder &&
        salesOrder.SalesLines &&
        salesOrder.SalesLines.map(salesLine => {
            const quantity = salesLine.Quantity;
            console.log('SalesLines', salesLine.DeliveryMode);
            orderSummaryitemsCount = quantity ? orderSummaryitemsCount + quantity : orderSummaryitemsCount;
        });

    useEffect(() => {
        salesLines &&
            salesLines.length > 0 &&
            _getProductsVendorInfo(
                actionContext,
                products,
                salesLines,
                dobbiesLabel,
                notAvailableText,
                SetVendorProducts,
                SetVendorInfo,
                vendorProducts,
                vendorsInfo
            ).catch(err => {
                return;
            });
        _configureAddresses(groups, SetPickedUpGroup, pickedUpGroup, SetDeliveryGroup, deliveryGroup);
        const validProductIds: number[] = [];
        products?.map(prod => {
            validProductIds.push(prod.RecordId);
        });
        getDeliveryOptionsAsync({ callerContext: props.context.actionContext, queryResultSettings: {} }, validProductIds)
            .then(res => {
                setDeliveryOptions(res);
            })
            .catch(e => {});

        // Stores Info
        const orgUnitLocationsInput = new GetAllOrgUnitLocationsByAreaInput(31.5070767, 74.4315609, 0, 0, false);
        getAllOrgUnitLocationsByArea(orgUnitLocationsInput, props.context.actionContext).then(stores => {
            setAllStores(stores);
        });
    }, []);

    const createdDateTime = salesOrder && salesOrder.SalesLines && salesOrder.SalesLines[0] && salesOrder.SalesLines[0].SalesDate;
    const locale = get(props, 'context.request.locale') || 'en-US';
    const orderCreatedDate = (createdDateTime && new Date(createdDateTime).toLocaleDateString(locale)) || '';
    const created = {
        dateLabel: orderPlacedOnLabel,
        orderCreatedDate: orderCreatedDate
    };
    // home delivery address shown at top
    const deliveryDetails = deliveryGroup !== -1 && groups && groups.groups[deliveryGroup].delivery;
    /* VSI Customization - END - 18/12/20 */
    return (
        <Module {...moduleProps}>
            <Node tag='div' className={`row ${isAuthenticated ? 'signin-order-confirmation' : 'signout-order-confirmation'}`}>
                {/* {isAuthenticated && (
                    <Node tag='div' className='col-12'>
                        {orderInfomation && heading}
                    </Node>
                )} */}
                <Node tag='div' className='col-12 order-confirmation-alldetails'>
                    {alert}
                    {loading}
                    {/* VSI Customization - START - 23/11/20 */}
                    {!alert && (
                        <div className='row'>
                            <Node tag='div' className='col-12'>
                                {orderInfomation && heading}
                            </Node>
                        </div>
                    )}
                    {!alert && (
                        <>
                            <p className='order-confirmation__email'>
                                A confirmation email will be sent to{' '}
                                <span>{props.salesOrder?.ReceiptEmail ? props.salesOrder?.ReceiptEmail : ''}</span>
                            </p>

                            <a href='/' className='continue-shopping'>
                                Continue shopping
                            </a>
                        </>
                    )}
                    {orderInfomation && (
                        <OrderConfirmationOrderInfomation {...orderInfomation} {...salesOrderInfo} {...created} {...props} />
                    )}
                    {deliveryDetails && isAuthenticated ? (
                        <>
                            <OrderConfirmationGroupDelivery
                                {...deliveryDetails}
                                {...props}
                                vendorsInfo={vendorsInfo}
                                deliveryOptions={deliveryOptions}
                            />
                        </>
                    ) : (
                        <OrderConfirmationGroupDeliveryGuest {...props} vendorsInfo={vendorsInfo} deliveryOptions={deliveryOptions} />
                    )}
                    {/* Following function just displays shipped items */}
                    {/* {vendorsInfo && _displayVendorProducts(props, vendorsInfo)} */}
                    {/* Following functions just displays pickedup items */}
                    {groups && _renderPickedOrders(props, groups, allStores)}
                    {/* VSI Customization - END - 23/12/20 */}

                    {/* Payment */}

                    {props?.payment && (
                        <>
                            <OrderConfirmationPayment
                                {...{ ...props.payment, paymentCardImages: props.config.paymentCardImages || [], ...props }}
                            />
                        </>
                    )}
                </Node>
                <Node tag='div' className='col-12 bottom-section'>
                    {orderSummary && (
                        <OrderConfirmationOrderSummary
                            {...orderSummary}
                            orderSummaryitemsCount={orderSummaryitemsCount}
                            isCnCCart={isCnCCart}
                        />
                    )}
                </Node>
                <div className='col-12 text-center'>
                    <a href='/' className='msc-btn btn-default'>
                        Continue shopping
                    </a>
                </div>
            </Node>
        </Module>
    );
};

/* VSI Customization - START - 18/12/20 */
const _displayHeadingRow = (
    resources: IOrderConfirmationResources,
    vendorInfo: IVendorProducts | undefined,
    index: number,
    total: number
) => {
    const { deliveryHeading, shippedOrderIndex, dispatchedByLabel, dobbiesLabel, shippingTimeLabel } = resources;
    const orderIndex = shippedOrderIndex && shippedOrderIndex.replace('{index}', index.toString()).replace('{total}', total.toString());
    return (
        <Node tag='div' className='ms-heading row delivery-dispatched-details'>
            <Node tag='div' className='delivery-dispatched-details__count'>
                <Node tag='span' className='__heading'>
                    {deliveryHeading}{' '}
                </Node>
                {total > 1 && (
                    <Node tag='span' className='__index'>
                        {orderIndex}
                    </Node>
                )}
            </Node>
            <Node tag='div' className='delivery-dispatched-details__right'>
                <Node tag='div' className='dispatched-by-vendor'>
                    <Node tag='span' className='label'>
                        {dispatchedByLabel}{' '}
                    </Node>
                    <Node tag='span' className='vendor-name'>
                        {vendorInfo ? vendorInfo.name : dobbiesLabel}
                    </Node>
                </Node>
                {vendorInfo && vendorInfo.time && (
                    <Node tag='div' className='estimated-shipping-time'>
                        <Node tag='span' className='label'>
                            {shippingTimeLabel}{' '}
                        </Node>
                        <Node tag='span' className='vendor-name'>
                            {vendorInfo.time}
                        </Node>
                    </Node>
                )}
            </Node>
        </Node>
    );
};
// Display shipped products
const _displayVendorProducts = (
    props: IOrderConfirmationViewProps & IOrderConfirmationProps<{}>,
    vendorsInfo: IVendorProducts[],
    deliveryOptions?: ProductDeliveryOptions[]
) => {
    const { deliveryHeading, shippedOrderIndex, dispatchedByLabel, dobbiesLabel, shippingTimeLabel } = props.resources;

    return (
        <Node tag='div' className='ms-vendor-shipped-products'>
            {vendorsInfo &&
                vendorsInfo.map((vendorInfo: IVendorProducts, index: number) => {
                    // filter shipped products from each sales line
                    const vendorsSalesLines =
                        props.salesLines &&
                        props.salesLines.filter(salesLine => {
                            const salesLineProductID = salesLine.data.product && salesLine.data.product.RecordId;
                            // filtering vendor's product of current index of vendorInfo
                            const isSameVendor = vendorInfo.productList.find(vendorProductID => {
                                return vendorProductID === salesLineProductID;
                            });
                            return (
                                isSameVendor &&
                                salesLine &&
                                salesLine.data.salesLine.DeliveryMode !== props.context.request.channel?.PickupDeliveryModeCode
                            );
                        });
                    const currentIndex = index + 1; // 1..n instead of 0...n-1
                    console.log('vendorsSalesLines', vendorsSalesLines);
                    const additionalMsgConfig = props.context.actionContext.requestContext.app.config.additionalDeliveryMessages;
                    let additionalMsgForDel = '';
                    return (
                        <Node tag='div' className='ms-dobies-products' key={index}>
                            {/* {_displayHeadingRow(props.resources, vendorInfo, currentIndex, vendorsInfo.length)} */}
                            {vendorsSalesLines &&
                                vendorsSalesLines.map((vendorsSalesLine, salesIndex) => {
                                    const salesLineItem = vendorsSalesLine.data.salesLine;
                                    const simpleProduct = vendorsSalesLine.data.product;
                                    console.log('vendorsSalesLine.data', vendorsSalesLine.data);

                                    let deliveryMode = vendorsSalesLine.data.salesLine.DeliveryMode;
                                    let deliveryDescription = '';
                                    deliveryOptions?.map(option => {
                                        option.DeliveryOptions?.map(delOption => {
                                            if (delOption.Code === deliveryMode) {
                                                deliveryDescription = delOption.Description || '';
                                            }
                                        });
                                    });
                                    additionalMsgForDel = additionalMsgConfig?.find(
                                        (delMsgForMode: { delMode: string; msg: string }) => delMsgForMode.delMode === deliveryMode
                                    )?.msg;
                                    if (deliveryMode === 'Common MOD') {
                                        const deliveryModeForCartlineInfo = deliveryOptions
                                            ?.find(delOpt => delOpt.ProductId === salesLineItem.ProductId)
                                            ?.DeliveryOptions?.find(
                                                dopt =>
                                                    dopt.Code !== props.context.request.channel?.PickupDeliveryModeCode &&
                                                    dopt.Code !== 'Common MOD'
                                            );
                                        if (deliveryModeForCartlineInfo?.Code) {
                                            deliveryMode = deliveryModeForCartlineInfo?.Code;
                                            additionalMsgForDel = additionalMsgConfig?.find(
                                                (delMsgForMode: { delMode: string; msg: string }) => delMsgForMode.delMode === deliveryMode
                                            )?.msg;
                                            deliveryDescription = deliveryModeForCartlineInfo?.Description || '';
                                        }
                                    }
                                    const delayedDelMsg = salesLineItem?.ShippingAddress?.ZipCode
                                        ? getDelayedDeliveryMsg(props.context.actionContext, salesLineItem?.ShippingAddress?.ZipCode)
                                        : '';
                                    const estDelivery = getDeliveryDateRange(new Date(), props.context.actionContext, deliveryMode || '');
                                    return (
                                        <div className='hd_product'>
                                            <Node className='sales-line-section' tag='div' key={index}>
                                                {simpleProduct && _displaySalesLine(salesLineItem, simpleProduct, props)}
                                                {/* {_displaySalesLinePrice(salesLineItem, props)} */}
                                            </Node>
                                            <div className='order-confirmation__HD-product-deliveryDetails'>
                                                <p> Dispatched by {vendorInfo ? vendorInfo.name : dobbiesLabel}</p>
                                                <p>Delivery: {additionalMsgForDel ? additionalMsgForDel : deliveryDescription}</p>
                                                <p>Estimated arrival by: {estDelivery?.maxDeliveryDate}</p>
                                                <p>{`${delayedDelMsg}`}</p>
                                            </div>
                                        </div>
                                    );
                                })}
                        </Node>
                    );
                })}
        </Node>
    );
};

// This function stores picked up groups in an array if any and stores delivery address to display on top if any
const _configureAddresses = (
    groups: IGroups | undefined,
    SetPickedUpGroup: React.Dispatch<React.SetStateAction<number[]>>,
    pickedUpGroup: number[],
    SetDeliveryGroup: React.Dispatch<React.SetStateAction<number>>,
    deliveryGroup: number
) => {
    if (pickedUpGroup.length > 0 || deliveryGroup !== -1) {
        return;
    }
    let deliveryAddressGroup = -1;
    const pickedUpList: number[] = [];
    groups &&
        groups.groups.map((group: IGroup, index: number) => {
            const addresstype = group.data.deliveryType.trim().toLowerCase();
            if (addresstype === 'ship') {
                deliveryAddressGroup = index;
            } else if (addresstype === 'pickup') {
                pickedUpList.push(index);
            }
        });
    if (deliveryAddressGroup !== deliveryGroup) {
        SetDeliveryGroup(deliveryAddressGroup);
    }
    if (pickedUpList !== pickedUpGroup) {
        SetPickedUpGroup(pickedUpList);
    }
};

// Returns array filled with vendor and their product details
const _getVendorInfo = (
    hybridProducts: SimpleProduct[],
    vendorsProducts: SimpleProduct[],
    salesLines: ISalesLine[],
    dobbiesLabel: string
): IVendorProducts[] => {
    if (hybridProducts && hybridProducts.length && hybridProducts.length > 0) {
        const vendorList: IVendorProducts[] = [];
        // store all vendors name or undefined if not
        vendorsProducts &&
            vendorsProducts.map(vendorsProduct => {
                const vendorNameProperty =
                    vendorsProduct.ExtensionProperties &&
                    vendorsProduct.ExtensionProperties.find(property => {
                        const propertykey = property.Key && property.Key.trim().toLowerCase();
                        return propertykey === 'vendorname';
                    });
                const vendorName = vendorNameProperty && vendorNameProperty.Value && vendorNameProperty.Value.StringValue;
                // check if vendor name is already stored in vendor list
                const isDuplicate = vendorList.find(vendor => {
                    return vendor && vendor.name === vendorName;
                });
                if (!isDuplicate) {
                    const shippingTimeProperty =
                        vendorsProduct.ExtensionProperties &&
                        vendorsProduct.ExtensionProperties.find(property => {
                            const propertykey = property.Key && property.Key.trim().toLowerCase();
                            return propertykey === 'estimatedshippingtime';
                        });
                    const shippingTime = shippingTimeProperty && shippingTimeProperty.Value && shippingTimeProperty.Value.StringValue;
                    // avoid duplicate vendor names
                    vendorList.push({ name: vendorName, time: shippingTime, productList: [] });
                }
            });
        // to store dobbies products, we need to check first vendor products
        const vendorsProductList: number[] = [];
        // set respective products indexes along with vendor name
        vendorList.map((vendorName: IVendorProducts, index: number) => {
            vendorsProducts &&
                vendorsProducts.map((product: SimpleProduct, pindex: number) => {
                    // returns products having vendor name matched with vendorList created above
                    product.ExtensionProperties &&
                        product.ExtensionProperties.map(extensionProperty => {
                            const key = extensionProperty.Key && extensionProperty.Key.trim().toLowerCase();
                            const value = extensionProperty.Value && extensionProperty.Value.StringValue;
                            if (key === 'vendorname' && value === vendorName.name) {
                                vendorList[index].productList.push(product.RecordId);
                                vendorsProductList.push(product.RecordId);
                            }
                        });
                });
        });
        // Filter shipped salesLines
        const shippedSalesLines = salesLines.filter(salesLine => {
            const deliveryType = salesLine.data.deliveryType.trim().toLowerCase();
            return deliveryType === 'ship';
        });
        // DOBBIES product list
        const dobbiesProducts: number[] = [];
        hybridProducts.map(hybridProduct => {
            const recordID = hybridProduct.RecordId;
            // exclude vendor products to add in dobbies' list
            const isVendorProduct = vendorsProductList.find(vendorProductId => {
                return recordID === vendorProductId;
            });
            if (isVendorProduct === undefined) {
                // exclude pickup products from dobbies to store only shipped items against it
                const isShipped = shippedSalesLines.find(salesLine => {
                    const productID = salesLine.data.product && salesLine.data.product.RecordId;
                    return recordID === productID;
                });
                if (isShipped) {
                    dobbiesProducts.push(hybridProduct.RecordId);
                }
            }
        });
        if (dobbiesProducts.length !== 0) {
            // add dobbies shipped items at the end of array
            const dobbiesProductObj: IVendorProducts = { name: dobbiesLabel, time: undefined, productList: dobbiesProducts };
            vendorList.push(dobbiesProductObj);
        }
        return vendorList;
    }
    return [];
};

export const OrderConfirmationOrderSummary: React.FC<IExtendedOrderSummary> = ({
    orderSummaryProps,
    heading,
    subtotal,
    shipping,
    tax,
    totalAmount,
    earnedPoints,
    orderSummaryitemsCount,
    isCnCCart
}) => {
    console.log('OS Shipping', shipping);
    const shippingElem = shipping as React.ReactElement;
    const deliveryCost =
        shipping &&
        React.cloneElement(shippingElem, {
            label: 'Delivery cost:',
            priceContext: { ...shippingElem.props.priceContext, freePriceText: 'Free' }
        });
    const subTotalElem = subtotal as React.ReactElement;
    const subTotalCost =
        subtotal &&
        React.cloneElement(subTotalElem, {
            label: 'Subtotal:'
        });
    return (
        <Node {...orderSummaryProps}>
            <p className='ms-order-confirmation__order-summary-heading'>
                Order summary ({orderSummaryitemsCount} {orderSummaryitemsCount === 1 ? 'item' : 'items'})
            </p>
            {/* {heading} */}
            {subTotalCost}
            {/* {subtotal} */}
            {deliveryCost}
            {isCnCCart ? (
                <div className='msc-order-summary__line-delivery-cost'>
                    <p>
                        <span className='msc-order-summary__label'>Click & collect:</span>
                        <span className='msc-order-summary__value'>Free</span>
                    </p>
                </div>
            ) : null}
            {tax}
            {totalAmount}
            {earnedPoints}
        </Node>
    );
};
// Get vendor attributes using data action and hybrids products with information
const _getProductsVendorInfo = async (
    actionContext: IActionContext,
    products: SimpleProduct[] | undefined,
    salesLines: ISalesLine[],
    dobbiesLabel: string,
    notAvailableText: string,
    SetVendorProducts: React.Dispatch<React.SetStateAction<SimpleProduct[]>>,
    SetVendorInfo: React.Dispatch<React.SetStateAction<IVendorProducts[]>>,
    vendorsProducts: SimpleProduct[],
    vendorsInfo: IVendorProducts[]
) => {
    const callerContext = actionContext;
    const {
        requestContext: {
            apiSettings: { channelId, catalogId }
        }
    } = actionContext;
    if (products) {
        const _hybridProducts: SimpleProduct[] = products;
        const vendorProducts: SimpleProduct[] = [];
        const searchCriteriaInput: ProductSearchCriteria = {};
        searchCriteriaInput.Context = { ChannelId: channelId, CatalogId: catalogId };
        const productIds: number[] = [];
        products.map(product => {
            productIds.push(product.RecordId);
        });
        searchCriteriaInput.Ids = productIds;
        searchCriteriaInput.IncludeAttributes = true;
        const productsData = await searchByCriteriaAsync({ callerContext, queryResultSettings: {} }, searchCriteriaInput);
        productsData.map((product: ProductSearchResult, index: number) => {
            const attributes = product.AttributeValues;
            const isvendorShippedAttribute =
                attributes &&
                attributes.find(attribute => {
                    const attributeName = attribute.Name && attribute.Name.trim().toLowerCase();
                    return attributeName === 'isvendershipproduct';
                });
            const isVendorShiped =
                isvendorShippedAttribute &&
                isvendorShippedAttribute.TextValue &&
                isvendorShippedAttribute.TextValue.trim().toLowerCase() === 'yes';
            const extensionProperties: CommerceProperty[] = [];
            if (isVendorShiped) {
                const selectedAttributes =
                    attributes &&
                    attributes.filter(attribute => {
                        const attributeName = attribute.Name && attribute.Name.trim().toLowerCase();
                        const isVendorNameAvailable = attribute.TextValue; // if vendor name is not undefined
                        return (
                            attributeName &&
                            isVendorNameAvailable &&
                            (attributeName === 'vendorname' || attributeName === 'estimatedshippingtime')
                        );
                    });
                if (selectedAttributes && selectedAttributes.length > 0) {
                    selectedAttributes &&
                        selectedAttributes.map(selectedAttribute => {
                            const commercePropertyValue: CommercePropertyValue = { StringValue: selectedAttribute.TextValue };
                            const commerceProperty: CommerceProperty = { Key: selectedAttribute.Name, Value: commercePropertyValue };
                            extensionProperties.push(commerceProperty);
                            // hybrid all products
                            _hybridProducts[index].ExtensionProperties = extensionProperties;
                            // no duplicate vendor products
                            const isDuplicate =
                                vendorProducts &&
                                vendorProducts.find(vendorProduct => {
                                    return vendorProduct.RecordId === product.RecordId;
                                });
                            // if product is vendorshipped, store it in array
                            !isDuplicate && vendorProducts.push(_hybridProducts[index]);
                        });
                } else {
                    // When product is vendor shipped but vendor info is not available
                    const vendorname: CommerceProperty = { Key: 'vendorname', Value: { StringValue: notAvailableText } };
                    const estimatedShippingTime: CommerceProperty = {
                        Key: 'estimatedshippingtime',
                        Value: { StringValue: notAvailableText }
                    };
                    extensionProperties.push(vendorname);
                    extensionProperties.push(estimatedShippingTime);
                    // hybrid all products
                    _hybridProducts[index].ExtensionProperties = extensionProperties;
                    // no duplicate vendor products, merge product with same vendor
                    const isDuplicate =
                        vendorProducts &&
                        vendorProducts.find(vendorProduct => {
                            return vendorProduct.RecordId === product.RecordId;
                        });
                    // if product is vendorshipped, store it in array
                    !isDuplicate && vendorProducts.push(_hybridProducts[index]);
                }
            }
        });

        if (!vendorsProducts || vendorsProducts.length === 0) {
            SetVendorProducts(vendorProducts);
        }
        const _vendorsInfo = _hybridProducts && _getVendorInfo(_hybridProducts, vendorProducts, salesLines, dobbiesLabel);
        if (!vendorsInfo || vendorsInfo.length === 0) {
            SetVendorInfo(_vendorsInfo);
        }
    }
};
/* VSI Customizatoin - END */

export default OrderConfirmationView;
